import { notification } from 'antd';
import { Dispatch } from '@reduxjs/toolkit';
import ApiService from 'utils/ApiCalls';
import evidencesDefSlice from './evidencesSlice';
import { IEvidenceResult, IEvidencesToShow, IEvidenceToShow } from './types';
import { LanguageList } from '../globalTypes';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';

const {
  set_loading,
  set_evidences,
  set_evidences_to_show,
  delete_evidence,
  add_new_evidence,
  edit_evidence,
  reset_evidences_to_save
} = evidencesDefSlice.actions;

const fetchEvidences = async (
  lang: LanguageList,
  filters: Record<string, string[]>
) => {
  try {
    const response = await ApiService.getEvidences(lang, filters);

    // Process the response to ensure file data is properly formatted
    if (response.data && Array.isArray(response.data)) {
      return response.data.map((evidence: any) => {
        // Process each evidence item to ensure file data is properly formatted
        if (evidence.SOLUTIONS) {
          Object.keys(evidence.SOLUTIONS).forEach((solKey) => {
            const solution = evidence.SOLUTIONS[solKey];
            if (solution.EVIDENCES) {
              Object.keys(solution.EVIDENCES).forEach((evdKey) => {
                const evidenceItem = solution.EVIDENCES[evdKey];
                if (evidenceItem.evd_file) {
                  // Ensure evd_file is properly formatted as UploadFile array
                  if (typeof evidenceItem.evd_file === 'string') {
                    try {
                      evidenceItem.evd_file = JSON.parse(evidenceItem.evd_file);
                    } catch (e) {
                      evidenceItem.evd_file = [];
                    }
                  }

                  // Process file array to ensure proper structure
                  if (Array.isArray(evidenceItem.evd_file)) {
                    evidenceItem.evd_file = evidenceItem.evd_file.map(
                      (file: any, index: number) => {
                        // If file is just a string (filename), create proper UploadFile object
                        if (typeof file === 'string') {
                          const fileExtension = file
                            .split('.')
                            .pop()
                            ?.toLowerCase();
                          // Assume all files are images - let browser handle detection
                          const isImage = true;
                          return {
                            uid: `file-${evidenceItem.evd_id}-${index}`,
                            name: file,
                            status: 'done',
                            type: isImage
                              ? `image/${
                                  fileExtension === 'jpg'
                                    ? 'jpeg'
                                    : fileExtension
                                }`
                              : undefined,
                            url: `/evidences/download/${evidenceItem.evd_id}?filename=${file}`,
                            response: { filename: file }
                          };
                        }
                        // If file is already an object, ensure it has proper URL
                        return {
                          uid:
                            file.uid || `file-${evidenceItem.evd_id}-${index}`,
                          name: file.name || file.filename || file,
                          status: 'done',
                          url:
                            file.url ||
                            file.file_url ||
                            file.path ||
                            `/evidences/download/${
                              evidenceItem.evd_id
                            }?filename=${file.name || file}`,
                          response: file
                        };
                      }
                    );
                  }
                }
              });
            }
          });
        }
        return evidence;
      });
    }

    return response.data || [];
  } catch (error) {
    console.error('Error fetching evidences:', error);
    return [];
  }
};

const setEvidences =
  (evidences: IEvidenceResult[]) => async (dispatch: Dispatch) => {
    try {
      dispatch(set_loading());
      dispatch(set_evidences(evidences));
    } catch (error) {
      console.error(error);
    }
  };

const setEvidencesToShow =
  (evidences: {
    [key: number]: { [key: number | string]: IEvidencesToShow };
  }) =>
  async (dispatch: Dispatch) => {
    try {
      dispatch(set_loading());
      dispatch(set_evidences_to_show(evidences));
    } catch (error) {
      console.error(error);
    }
  };

const editEvidenceValue =
  (
    evidenceId: string | number,
    evidenceData: IEvidenceToShow,
    key: string,
    value: string
  ) =>
  (dispatch: Dispatch) => {
    try {
      dispatch(edit_evidence({ evidenceId, evidenceData, key, value }));
    } catch (error) {
      console.error(error);
    }
  };

const deleteEvidence = async (
  evdId: number | string,
  lang: LanguageList,
  filters: Record<string, string[]>
) => {
  try {
    const response = await ApiService.removeEvidenceDef(evdId);

    if (response.status === 200) {
      const updatedData = await fetchEvidences(lang, filters);

      notification.success({
        message: response.status,
        description: response?.data?.message || 'Evidence successfully deleted',
        duration: 20
      });

      return updatedData;
    }
  } catch (error: any) {
    notification.error({
      message: 'Delete failed',
      description:
        error?.response?.data?.message ||
        error?.message ||
        'Failed to delete evidence',
      duration: 20
    });

    console.error(error);
    throw error;
  }
};

const handleDeleteClick =
  (clnReqId: number, evId: number | string) => (dispatch: Dispatch) => {
    try {
      dispatch(set_loading());
      dispatch(delete_evidence({ clnReqId, evId }));
    } catch (error) {
      console.error(error);
    }
  };

const approveEvidence = async (evdId: string | number) => {
  try {
    await ApiService.approveEvidence(evdId);
  } catch (error: any) {
    notification.error({
      message: 'Approve failed',
      description:
        error?.response?.data?.message ||
        error?.message ||
        'Failed to approve evidence',
      duration: 6
    });
  }
};

const rejectEvidence = async (evdId: string | number) => {
  try {
    await ApiService.rejectEvidence(evdId);
  } catch (error: any) {
    notification.error({
      message: 'Reject failed',
      description:
        error?.response?.data?.message ||
        error?.message ||
        'Failed to reject evidence',
      duration: 6
    });
  }
};

const fetchEvidenceDefApproversList = async () => {
  try {
    const response = await ApiService.getEvidenceDefApproverList();

    return response.data;
  } catch (error) {
    console.error(error);
  }
};

const addNewEvidence =
  (cln_req_id: number, sol_id: number) => (dispatch: Dispatch) => {
    try {
      dispatch(set_loading());
      dispatch(add_new_evidence({ cln_req_id, sol_id }));
    } catch (error) {
      console.error(error);
    }
  };

const saveEvidences = async (
  evidencesToSave: IEvidenceToShow[],
  lang: LanguageList
) => {
  try {
    const transformedEvidences = evidencesToSave
      .filter(
        (evidence) => (evidence as any).bru_id && (evidence as any).sol_id
      )
      .map((evidence) => {
        const today = new Date().toISOString().split('T')[0];
        const isExistingEvidence = typeof evidence.evd_id === 'number';

        // Extract filenames from uploaded files
        let uploadedFilenames: string[] = [];

        // Check if evd_file is a string (JSON) and parse it
        if (typeof evidence.evd_file === 'string') {
          try {
            const parsedFiles = JSON.parse(evidence.evd_file);
            uploadedFilenames = Array.isArray(parsedFiles)
              ? parsedFiles.filter((file) => file.name).map((file) => file.name)
              : [];
          } catch (e) {
            uploadedFilenames = [];
          }
        } else if (Array.isArray(evidence.evd_file)) {
          uploadedFilenames = evidence.evd_file
            .filter((file) => file.name)
            .map((file) => file.name);
        }

        if (isExistingEvidence) {
          // For existing evidence, keep evd_id and due_date, provide default dates if null
          return {
            ...evidence,
            evd_id: evidence.evd_id,
            evd_approver_ref:
              evidence.evd_approver_ref?.replace('mailto:', '') ||
              evidence.evd_approver_ref,
            evd_valid_from: evidence.evd_valid_from || today,
            evd_valid_to: evidence.evd_valid_to || '9999-01-01',
            due_date: evidence.due_date || 'None',
            evd_file: uploadedFilenames
          };
        } else {
          // For new evidence, remove evd_id and due_date, provide default dates
          const { evd_id, due_date, ...cleanEvidence } = evidence;
          return {
            ...cleanEvidence,
            evd_id: generateUniqueId(),
            evd_approver_ref:
              evidence.evd_approver_ref?.replace('mailto:', '') ||
              evidence.evd_approver_ref,
            evd_valid_from: evidence.evd_valid_from || today,
            evd_valid_to: evidence.evd_valid_to || '9999-01-01',
            evd_file: uploadedFilenames
          };
        }
      });

    const response = await ApiService.saveEvidencesDef(
      transformedEvidences as any,
      lang
    );

    notification.success({
      message: response.status,
      description: response?.data?.message || 'Evidences successfully saved',
      duration: 20
    });

    return response;
  } catch (error: any) {
    notification.error({
      message: 'Save failed',
      description:
        error?.response?.data?.message ||
        error?.message ||
        'Failed to save evidences',
      duration: 6
    });
    throw error;
  }
};

const resetEvidencesToSave = () => (dispatch: Dispatch) => {
  try {
    dispatch(reset_evidences_to_save());
  } catch (error) {
    console.error(error);
  }
};

export default {
  fetchEvidences,
  setEvidences,
  deleteEvidence,
  setEvidencesToShow,
  handleDeleteClick,
  fetchEvidenceDefApproversList,
  addNewEvidence,
  editEvidenceValue,
  approveEvidence,
  rejectEvidence,
  saveEvidences,
  resetEvidencesToSave
};
